import React from 'react';
import '../../styles/components/AsperaImagePicker.scss';
import { Button } from '../Carbon';
import { language } from '../../services/LanguageService';
import { notification } from '../../services/NotificationService';
import { AsperaError } from '../../classes/AsperaError';
/**
 * React Component
 *
 * The AsperaImagePicker provides a basic image picker system
 *
 * @example
 * ```javascript
 * <AsperaImagePicker />
 * ```
 */
export class AsperaImagePicker extends React.Component {
    constructor() {
        super(...arguments);
        this.currentData = {};
        this.parseFile = (file) => {
            const { maxSize } = this.props;
            if (!file) {
                new AsperaError('AsperaImagePicker: parse file did not receive a valid file', { file });
                return;
            }
            if (file.type.indexOf('image') === -1) {
                notification.create('error', language.get('imagePicker.invalidFileType'), language.get('imagePicker.onlyImagesSupported'));
                return;
            }
            if (maxSize) {
                if (file.size > maxSize) {
                    notification.create('error', language.get('imagePicker.fileTooBig'));
                    this.clearFileChooser();
                    return;
                }
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                this.updateImage(reader.result, file.name, true);
            };
            reader.onerror = error => {
                new AsperaError('AsperaImagePicker: Unable to parse image', error);
                notification.create('error', language.get('imagePicker.unableToProcessImage'));
            };
        };
        this.chooseImage = (event) => {
            const target = event === null || event === void 0 ? void 0 : event.target;
            if (event && target && target.files && target.files.length) {
                const file = target.files[0];
                this.parseFile(file);
            }
        };
        this.clearFileChooser = () => {
            if (this.element)
                this.element.value = '';
        };
        this.splitImage = (image) => {
            const split = image.split(';base64,');
            return {
                header: split[0],
                body: split[1],
            };
        };
        this.clickElement = () => {
            if (this.element)
                this.element.click();
        };
        this.removeImage = () => {
            const { onChange } = this.props;
            this.currentData = {};
            this.backupInitialImage = undefined;
            onChange({
                data: null,
                type: null,
                body: null,
            });
            this.setState({});
        };
        this.handleDrop = (event) => {
            var _a, _b;
            if (event && typeof event.stopPropagation === 'function' && typeof event.preventDefault === 'function') {
                event.stopPropagation();
                event.preventDefault();
                const droppedFile = (_b = (_a = event.dataTransfer) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b.item(0);
                this.parseFile(droppedFile);
            }
        };
    }
    updateImage(image, fileName, triggerOnChange) {
        const { onChange } = this.props;
        if (!image)
            return;
        if (image.indexOf(';base64,') === -1) {
            this.backupInitialImage = image;
            this.setState({});
            return;
        }
        image = image.replace('data:', '');
        const splitData = this.splitImage(image);
        this.currentData = {
            data: image,
            type: splitData.header,
            body: splitData.body,
            name: fileName,
        };
        this.setState({});
        if (triggerOnChange)
            onChange({
                data: image,
                type: splitData.header,
                body: splitData.body,
                name: fileName,
            });
    }
    componentDidMount() {
        const { initialData } = this.props;
        this.updateImage(initialData);
    }
    componentDidUpdate(oldProps) {
        const { initialData } = this.props;
        if (initialData !== oldProps.initialData) {
            this.updateImage(initialData);
        }
    }
    render() {
        const { className, roundImage, renderHeight, renderWidth, displayType } = this.props;
        const imageStyle = {
            width: `${renderWidth || 200}px`,
            height: `${renderHeight || 200}px`,
            backgroundImage: this.currentData.data ? `url(data:${this.currentData.data})` : this.backupInitialImage ? `url(${this.backupInitialImage})` : undefined,
            backgroundSize: displayType ? displayType : 'contain',
        };
        const hasImage = !!(this.currentData.data || this.backupInitialImage);
        return (React.createElement("div", { className: `aspera-image-picker ${className ? className : ''}`, "data-auto": "aspera-image-picker" },
            React.createElement(Button, { className: "image-button", onClick: (this.clickElement), kind: "tertiary" }, language.get('imagePicker.addFile')),
            hasImage && React.createElement(Button, { className: "image-button", onClick: (this.removeImage), kind: "ghost" }, language.get('imagePicker.remove')),
            React.createElement("div", { className: `image-area ${roundImage ? 'rounded-image' : ''}`, onDrop: this.handleDrop, style: imageStyle },
                !hasImage && React.createElement("div", { className: "drop-text" }, language.get('imagePicker.dropImageHere')),
                React.createElement("input", { className: "image-file-area", ref: el => {
                        this.element = el;
                    }, type: "file", onChange: this.chooseImage, value: "" }))));
    }
}
